import { Button, Layout } from "matsuri-ui"
import { Navigate, useNavigate, useParams, useRoutes } from "react-router-dom"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback, useEffect, useMemo, useState } from "react"
import { PhotoPageLayout } from "../../../templates/PhotoPageLayout"
import { useErrandProgress } from "./useErrandProgress"
import { Errand } from "@/model/cleaningInfo"
import { TakePhotoCover } from "@/components/TakePhotoCover/TakePhotoCover"
import { assertIsDefined } from "@/utils/assetIsDefined"
import {
    useCleaningInfoDetail,
    useUploadErrandPicture
} from "@/hooks/useCleaningInfos"
import { useTranslation } from "@/hooks/useTranslation"
import { stringToBlob } from "@/utils/image"
import { genCleaningReportPagePath } from "@/routes/protected"

const useErrandIdFromParam = () => {
    const { errandId } = useParams<"errandId">()
    assertIsDefined(errandId)
    return { errandId }
}

const ErrandUploadPage = (props: {
    undoneErrands: Errand[]
    currentCount: number
    totalCount: number
    handleReceiveFile: (errandId: string, blob: Blob) => void
    next: () => void
}) => {
    const { t } = useTranslation()
    const { errandId } = useErrandIdFromParam()
    const errand = props.undoneErrands.find(({ id }) => id === errandId)

    // お願いごとがないときは次のページに遷移させる。お願い事がない時にはこのページには遷移しないはずだが、
    // もし入ってきてしまった時には抜け出す術がないのでtotalCountとcurrentCountでチェックを行う
    if (props.totalCount === 0 || props.currentCount > props.totalCount) {
        props.next()
    }

    const { handleReceiveFile } = props
    const handleReceiveFileWithErrandId = useCallback(
        (data: string, mimetype: string) => {
            // TakePhotoCover が dataURL を返す I/F のため、ここで Blob に再構築する。
            // 受け側を Blob で統一しつつ TakePhotoCover の仕様変更は別タスクに分離する。
            const blob = stringToBlob(data, mimetype)
            handleReceiveFile(errandId, blob)
        },
        [errandId, handleReceiveFile]
    )

    return errand ? (
        <PhotoPageLayout
            headerTitle={t("お願い事撮影")}
            progressBar={{
                value: props.currentCount,
                max: props.totalCount
            }}
            page={{
                title: t("お願いごとの内容を撮影してください"),
                description: errand.title
            }}
        >
            <TakePhotoCover onChange={handleReceiveFileWithErrandId} />
        </PhotoPageLayout>
    ) : (
        <>loading...</>
    )
}

const ErrandConfirmPage = (props: {
    undoneErrands: Errand[]
    picture: Blob
    currentCount: number
    totalCount: number
    handleSubmit: (errandId: string, blob: Blob) => void
    handleRetry: (errandId: string) => void
}) => {
    const { t } = useTranslation()
    const { errandId } = useErrandIdFromParam()
    const errand = props.undoneErrands.find(errand => errand.id === errandId)

    const { picture, handleSubmit, handleRetry, currentCount } = props
    const objectUrl = useMemo(() => URL.createObjectURL(picture), [picture])
    useEffect(() => {
        return () => URL.revokeObjectURL(objectUrl)
    }, [objectUrl])

    const handleSubmitWithErrandId = useCallback(() => {
        handleSubmit(errandId, picture)
    }, [errandId, handleSubmit, picture])
    const handleRetryWithErrandId = useCallback(() => {
        handleRetry(errandId)
    }, [errandId, handleRetry])

    return errand ? (
        <PhotoPageLayout
            headerTitle={t("こちらでよろしいですか？")}
            progressBar={{
                value: currentCount,
                max: props.totalCount
            }}
            page={{
                title: t("お願いごとの内容を撮影してください"),
                description: errand.title
            }}
            footer={
                <Layout.Column gap={2}>
                    <div>
                        <Button
                            variant="filled"
                            color="primary"
                            fullWidth
                            onClick={handleSubmitWithErrandId}
                        >
                            OK
                        </Button>
                    </div>
                    <div>
                        <Button fullWidth onClick={handleRetryWithErrandId}>
                            {t("撮り直し")}
                        </Button>
                    </div>
                </Layout.Column>
            }
        >
            <img src={objectUrl} width="100%" height="256px" />
        </PhotoPageLayout>
    ) : (
        <>{t("お願い事の表示準備中...")}</>
    )
}

export const ErrandPage = () => {
    const navigate = useNavigate()
    const uploadErrandPicture = useUploadErrandPicture()
    const [picture, setPictureData] = useState<Blob | undefined>(undefined)
    const { cleaningId } = useParams<"cleaningId">()
    assertIsDefined(cleaningId)
    const checkitemsPath = genCleaningReportPagePath(cleaningId, "checkitems")

    const { token } = useAuth()
    const { data: cleaningDetail, refetch: refetchCleaningInfoDetail } =
        useCleaningInfoDetail(token, cleaningId)

    const onErrandUpload = useCallback(async () => {
        await refetchCleaningInfoDetail()
    }, [refetchCleaningInfoDetail])

    const { undoneErrands, totalCount, currentCount } = useErrandProgress(
        cleaningDetail?.errands || []
    )

    const handleReceiveFile = useCallback(
        (errandId: string, blob: Blob) => {
            setPictureData(blob)
            navigate(
                genCleaningReportPagePath(
                    cleaningId,
                    `errands/${errandId}/confirm`
                )
            )
        },
        [navigate, cleaningId]
    )

    const handleSubmit = useCallback(
        async (errandId: string, blob: Blob) => {
            await uploadErrandPicture({ errandId, blob })

            const anotherUndoneErrand = undoneErrands.find(
                errand => errand.id !== errandId
            )

            if (!anotherUndoneErrand) {
                navigate(checkitemsPath)
                return
            }

            onErrandUpload()

            navigate(
                genCleaningReportPagePath(
                    cleaningId,
                    `errands/${anotherUndoneErrand.id}/picture`
                )
            )
        },
        [
            uploadErrandPicture,
            undoneErrands,
            onErrandUpload,
            navigate,
            checkitemsPath,
            cleaningId
        ]
    )

    const handleRetry = useCallback(
        (errandId: string) => {
            navigate(
                genCleaningReportPagePath(
                    cleaningId,
                    `errands/${errandId}/picture`
                )
            )
        },
        [navigate, cleaningId]
    )

    const handleFinish = useCallback(() => {
        navigate(checkitemsPath)
    }, [navigate, checkitemsPath])

    const routes = [
        {
            path: ":errandId",
            children: [
                {
                    path: "picture",
                    element: (
                        <ErrandUploadPage
                            undoneErrands={undoneErrands}
                            totalCount={totalCount}
                            currentCount={currentCount}
                            handleReceiveFile={handleReceiveFile}
                            next={handleFinish}
                        />
                    )
                },
                {
                    path: "confirm",
                    element: !picture ? (
                        // 確認画面でリロードした時には未アップロード状態の画像が消えてしまうので、
                        // アップロード画面に戻す
                        <Navigate to="../picture" />
                    ) : (
                        <ErrandConfirmPage
                            undoneErrands={undoneErrands}
                            picture={picture}
                            handleSubmit={handleSubmit}
                            handleRetry={handleRetry}
                            currentCount={currentCount}
                            totalCount={totalCount}
                        />
                    )
                }
            ]
        },
        {
            index: true,
            element: (
                <ErrandNavigate
                    {...(cleaningDetail
                        ? {
                              loading: false,
                              undoneErrands,
                              checkitemsPath
                          }
                        : { loading: true })}
                />
            )
        }
    ]

    const element = useRoutes(routes)

    return element
}

const ErrandNavigate = (
    props:
        | { loading: true }
        | {
              loading: false
              undoneErrands: Errand[]
              checkitemsPath: string
          }
) => {
    if (props.loading) {
        return <>loading.....</>
    }

    if (props.undoneErrands.length === 0) {
        return <Navigate to={props.checkitemsPath} />
    }

    return <Navigate to={`${props.undoneErrands[0].id}/picture`} />
}
